import {
  type FC,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
} from "react";
import Spinner from "@/components/graphics/Spinner";
import type { CompanyNameLookupItem } from "@blanket/services/blanketApi";

interface CompanyNameSuggestionsProps {
  children: ReactNode;
  items: CompanyNameLookupItem[] | undefined;
  isLoading: boolean;
  onSelect: (item: CompanyNameLookupItem) => void;
  onDismiss: () => void;
}

export const CompanyNameSuggestions: FC<CompanyNameSuggestionsProps> = ({
  children,
  items,
  isLoading,
  onSelect,
  onDismiss,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isOpen = isLoading || (items != null && items.length > 0);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onDismiss();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen, onDismiss]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      onDismiss();
    }
  };

  return (
    <div
      ref={containerRef}
      className="position-relative blanket-scroll-safe-dropdown"
      onKeyDown={handleKeyDown}
    >
      {children}
      {isOpen ? (
        <ul
          role="listbox"
          className="blanket-dropdown-menu blanket-suggestions list-group mb-0"
        >
          {isLoading ? (
            <li className="list-group-item d-flex align-items-center gap-2 text-muted">
              <Spinner inline size={16} label="" />
              Søger...
            </li>
          ) : (
            items?.map((item) => (
              <li
                key={`${item.cvr}-${item.pNumbers?.join("-") ?? ""}`}
                role="option"
                tabIndex={0}
                className="list-group-item list-group-item-action d-flex justify-content-between gap-3"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => onSelect(item)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect(item);
                  }
                }}
              >
                <span>{item.companyName ?? "—"}</span>
                <span className="text-muted flex-shrink-0">CVR {item.cvr}</span>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
};
